<template>
  <!--
    What each service of a multi-service application costs (VSS Agent first). The deployment
    wizard's compatibility check reads these rows: the device resolves which services the chosen
    configuration starts, matches each one here and sums GPU / RAM / download against itself.
  -->
  <v-dialog :value="value" max-width="96vw" width="1500" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Component requirements · {{ docker ? docker.docker_name : '' }}</span>
        <v-spacer />
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle class="pt-1">
        A row applies to compose services whose name matches the pattern (and the env condition, if
        set) within the version range; the lowest priority wins. Rows marked <b>estimate</b> are
        shown to users as estimates.
      </v-card-subtitle>
      <v-card-text>
        <div class="d-flex align-center mb-3">
          <v-btn color="primary" small depressed @click="openEditor(null)">
            <v-icon left small>mdi-plus</v-icon> Add row
          </v-btn>
          <v-btn class="ml-2" small outlined :loading="exporting" @click="exportRows">
            <v-icon left small>mdi-download</v-icon> Export
          </v-btn>
          <v-btn class="ml-2" small outlined @click="openImport">
            <v-icon left small>mdi-upload</v-icon> Import
          </v-btn>
          <v-btn v-if="hasSeed" class="ml-2" small outlined :loading="resetting" @click="resetRows">
            <v-icon left small>mdi-restore</v-icon> Reset to defaults
          </v-btn>
          <v-spacer />
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="Search"
            style="max-width: 260px"
          />
        </div>
        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <v-data-table
          :headers="headers"
          :items="rows"
          :search="search"
          :loading="loading"
          :items-per-page="-1"
          :footer-props="{ 'items-per-page-options': [25, 50, -1] }"
          dense
          class="components-table"
          sort-by="priority"
        >
          <template #[`item.service_match`]="{ item }">
            <code>{{ item.service_match }}</code>
            <div v-if="item.env_match" class="caption grey--text">when {{ item.env_match }}</div>
          </template>
          <template #[`item.versions`]="{ item }">
            <span v-if="!item.version_min && !item.version_max" class="grey--text">All versions</span>
            <span v-else>{{ item.version_min || '…' }} – {{ item.version_max || '…' }}</span>
          </template>
          <template #[`item.vram_gb`]="{ item }">{{ num(item.vram_gb) }}</template>
          <template #[`item.ram_gb`]="{ item }">{{ num(item.ram_gb) }}</template>
          <template #[`item.download_gb`]="{ item }">{{ num(item.download_gb) }}</template>
          <template #[`item.label`]="{ item }">
            <span class="label-cell">{{ item.label }}</span>
          </template>
          <template #[`item.share`]="{ item }">
            <span v-if="item.gpu_fraction_env" :title="item.gpu_fraction_env">{{ item.gpu_default_fraction }}</span>
            <span v-else>—</span>
          </template>
          <template #[`item.source`]="{ item }">
            <v-chip x-small :color="item.source === 'measured' ? 'success' : 'warning'" text-color="white">
              {{ item.source }}
            </v-chip>
          </template>
          <template #[`item.measured`]="{ item }">
            <template v-if="measurements[item.id]">
              <span :title="measuredTitle(measurements[item.id])">
                RAM {{ num(measurements[item.id].ramMaxGb) }}
                <template v-if="measurements[item.id].vramMaxGb">
                  · VRAM {{ num(measurements[item.id].vramMaxGb) }}<span v-if="measurements[item.id].shareSized">*</span>
                </template>
                <span class="caption grey--text">({{ measurements[item.id].count }}×)</span>
              </span>
              <v-btn x-small text color="primary" :loading="applying === item.id" @click="applyMeasured(item)">Apply</v-btn>
            </template>
            <span v-else class="grey--text">—</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon small @click="openEditor(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="removeRow(item)"><v-icon small color="red">mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editor.open" max-width="760">
      <v-card>
        <v-card-title>{{ editor.form.id ? 'Edit row' : 'Add row' }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-text-field v-model="editor.form.service_match" label="Service name pattern (regex)" outlined dense hint="e.g. ^rtvi-embed$" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editor.form.priority" type="number" label="Priority (lower wins)" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="editor.form.env_match" label="Env condition (optional)" outlined dense hint="KEY=regex, several separated by ;  e.g. VLM_MODEL_TO_USE=^openai-compat$" persistent-hint />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field v-model="editor.form.label" label="Label shown to users" outlined dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="editor.form.role" :items="roleItems" label="Role" outlined dense hint="LLM/VLM rows are left out when that model is remote" persistent-hint />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.version_min" label="From version" outlined dense hint="inclusive" persistent-hint />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.version_max" label="Until version" outlined dense hint="exclusive" persistent-hint />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.vram_gb" type="number" label="VRAM (GB)" outlined dense />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.ram_gb" type="number" label="RAM (GB)" outlined dense />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.download_gb" type="number" label="Weights download (GB)" outlined dense />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="editor.form.gpu_default_fraction" type="number" step="0.05" label="Default GPU share" outlined dense hint="0–1, e.g. 0.9 for NIM" persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="editor.form.gpu_fraction_env" label="GPU share env vars" outlined dense hint="comma separated, e.g. NIM_GPU_MEM_FRACTION,NIM_KVCACHE_PERCENT" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="editor.form.source" :items="['estimate', 'measured']" label="Source" outlined dense />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field v-model="editor.form.notes" label="Notes" outlined dense />
            </v-col>
          </v-row>
          <v-alert v-if="editor.error" type="error" dense text class="mt-2 mb-0">{{ editor.error }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editor.open = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="editor.saving" @click="saveRow">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importer.open" max-width="760">
      <v-card>
        <v-card-title>Import rows</v-card-title>
        <v-card-text>
          <p class="mb-3">
            Paste or choose a file exported with <b>Export</b> (from this or another environment).
            Every row is checked first; a file with an error changes nothing.
          </p>
          <v-file-input
            accept="application/json,.json"
            label="JSON file"
            outlined
            dense
            prepend-icon="mdi-file-upload"
            @change="readImportFile"
          />
          <v-textarea v-model="importer.text" label="…or paste JSON" outlined rows="8" class="mono-text" />
          <v-radio-group v-model="importer.mode" class="mt-0">
            <v-radio value="merge" label="Merge: update rows with the same pattern, env condition and version range; add the rest" />
            <v-radio value="replace" label="Replace: delete every row of this application, then add the file's rows" />
          </v-radio-group>
          <v-alert v-if="importer.error" type="error" dense text class="mb-0">{{ importer.error }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="importer.open = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="importer.saving" :disabled="!importer.text" @click="runImport">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'

const EMPTY = {
  id: null,
  service_match: '',
  priority: 100,
  env_match: '',
  label: '',
  role: null,
  version_min: '',
  version_max: '',
  vram_gb: '',
  ram_gb: '',
  download_gb: '',
  gpu_fraction_env: '',
  gpu_default_fraction: '',
  source: 'estimate',
  notes: ''
}

export default Vue.extend({
  name: 'AppComponentsDialog',
  props: {
    value: { type: Boolean, default: false },
    docker: { type: Object, default: null }
  },
  data () {
    return {
      rows: [],
      hasSeed: false,
      measurements: {},
      applying: null,
      exporting: false,
      importer: { open: false, text: '', mode: 'merge', saving: false, error: '', result: '' },
      loading: false,
      resetting: false,
      error: '',
      search: '',
      editor: { open: false, saving: false, error: '', form: { ...EMPTY } },
      roleItems: [
        { text: '—', value: null },
        { text: 'LLM', value: 'llm' },
        { text: 'VLM', value: 'vlm' }
      ],
      headers: [
        { text: 'Prio', value: 'priority', width: 60 },
        { text: 'Service', value: 'service_match' },
        { text: 'Label', value: 'label' },
        { text: 'Role', value: 'role', width: 60 },
        { text: 'Versions', value: 'versions', sortable: false },
        { text: 'VRAM GB', value: 'vram_gb', width: 80 },
        { text: 'RAM GB', value: 'ram_gb', width: 80 },
        { text: 'Download GB', value: 'download_gb', width: 100 },
        { text: 'GPU share', value: 'share', sortable: false, width: 90 },
        { text: 'Source', value: 'source', width: 100 },
        { text: 'Measured (max)', value: 'measured', sortable: false },
        { text: '', value: 'actions', sortable: false, width: 96, align: 'end' }
      ]
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (open) {
        if (open) this.load()
      }
    }
  },
  methods: {
    num (v) {
      return v === null || v === undefined ? '—' : v
    },
    async load () {
      if (!this.docker) return
      this.loading = true
      this.error = ''
      try {
        const res = await apiService.get(`/admin-app-components/${this.docker.id}`)
        this.rows = res.data.data || []
        this.hasSeed = !!res.data.hasSeed
        this.measurements = res.data.measurements || {}
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.loading = false
    },
    measuredTitle (m) {
      const lines = [`${m.count} measurement(s), last ${m.lastAt || '?'}`]
      if (m.devices.length) lines.push(`Devices: ${m.devices.join(', ')}`)
      if (m.services.length) lines.push(`Services: ${m.services.join(', ')}`)
      if (m.shareSized) lines.push('* VRAM is the GPU share the service was given, not what it needed; Apply leaves VRAM alone.')
      return lines.join('\n')
    },
    async applyMeasured (row) {
      this.applying = row.id
      try {
        await apiService.post(`/admin-app-components/row/${row.id}/apply-measured`, {})
        await this.load()
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.applying = null
    },
    async exportRows () {
      this.exporting = true
      try {
        const res = await apiService.get(`/admin-app-components/${this.docker.id}/export`)
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' })
        const link = document.createElement('a')
        const slug = String(this.docker.docker_name || 'app').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        link.href = URL.createObjectURL(blob)
        link.download = `${slug}-components.json`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.exporting = false
    },
    openImport () {
      this.importer = { open: true, text: '', mode: 'merge', saving: false, error: '', result: '' }
    },
    readImportFile (file) {
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => { this.importer.text = String(reader.result || '') }
      reader.readAsText(file)
    },
    async runImport () {
      this.importer.error = ''
      let parsed
      try {
        parsed = JSON.parse(this.importer.text)
      } catch (err) {
        this.importer.error = `Not valid JSON: ${err.message}`
        return
      }
      const components = Array.isArray(parsed) ? parsed : parsed.components
      if (!Array.isArray(components)) {
        this.importer.error = 'Expected an exported file ({ "components": [...] }) or a list of rows.'
        return
      }
      if (this.importer.mode === 'replace') {
        const answer = await Swal.fire({
          title: 'Replace every row?',
          text: `All ${this.rows.length} rows of this application are deleted and the ${components.length} rows of the file are added.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Replace'
        })
        if (!answer.isConfirmed) return
      }
      this.importer.saving = true
      try {
        const res = await apiService.post(`/admin-app-components/${this.docker.id}/import`, { components, mode: this.importer.mode })
        const s = res.data.summary || {}
        this.importer.open = false
        await this.load()
        Swal.fire({ icon: 'success', title: 'Imported', text: `${s.added || 0} added, ${s.updated || 0} updated, ${s.removed || 0} removed.` })
      } catch (err) {
        this.importer.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.importer.saving = false
    },
    openEditor (row) {
      const form = { ...EMPTY }
      if (row) {
        for (const key of Object.keys(EMPTY)) {
          form[key] = row[key] === null || row[key] === undefined ? EMPTY[key] : row[key]
        }
      }
      this.editor = { open: true, saving: false, error: '', form }
    },
    async saveRow () {
      this.editor.saving = true
      this.editor.error = ''
      const { id, ...payload } = this.editor.form
      try {
        if (id) await apiService.put(`/admin-app-components/row/${id}`, payload)
        else await apiService.post(`/admin-app-components/${this.docker.id}`, payload)
        this.editor.open = false
        await this.load()
      } catch (err) {
        this.editor.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.editor.saving = false
    },
    async removeRow (row) {
      const answer = await Swal.fire({
        title: 'Delete this row?',
        text: row.service_match,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      })
      if (!answer.isConfirmed) return
      try {
        await apiService.delete(`/admin-app-components/row/${row.id}`)
        await this.load()
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
    },
    async resetRows () {
      const answer = await Swal.fire({
        title: 'Reset to the default values?',
        text: 'Every row of this application is replaced by the built-in starting values; measured values are lost.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reset'
      })
      if (!answer.isConfirmed) return
      this.resetting = true
      try {
        await apiService.post(`/admin-app-components/${this.docker.id}/reset`, {})
        await this.load()
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message
      }
      this.resetting = false
    }
  }
})
</script>

<style scoped>
.components-table >>> td {
  white-space: nowrap;
}

.components-table >>> code {
  white-space: normal;
  word-break: break-all;
}

.mono-text >>> textarea {
  font-family: monospace;
  font-size: 12px;
}

.label-cell {
  display: inline-block;
  min-width: 220px;
  white-space: normal;
}
</style>
